import { getBackendUrl } from "@Utils";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import axiosWrapper from "@AxiosWrapper";
import { resolveFileAccess, type FileAccessResult } from "@Services/utils/fileViewPermission";
import type { RootState } from "@Reducers/index";
import type { GetFolderRolesListResponse, SpaceToggleResponse } from "@ServerTypes/folders/folders.schema";

/**
 * The shared axios interceptor RESOLVES a transport failure (ERR_NETWORK) or a throttled
 * request (429) with a synthesized `{ status: false }` envelope instead of rejecting — see
 * `services/axiosShared.ts`. So any response can carry a false status at runtime, including
 * one whose schema declares `status: z.literal(true)`; the `status` checks below stay live
 * because the responses are read through this union.
 */
type WithNetworkFailure<T> = T | { status: false; errorCode?: number; errorMessage?: string };

/**
 * `catch` hands back `unknown`. Axios rejections carry the server's payload under
 * `response.data` and fall back to the error's own message — the order the thunks
 * below used to spell out with a pair of assertions each. Both checks are real runtime
 * tests (`isAxiosError` reads the flag axios sets), so nothing is asserted.
 */
function rejectionPayload(error: unknown): unknown {
  if (axios.isAxiosError<unknown>(error)) return error.response?.data || error.message;
  if (error instanceof Error) return error.message;
  return error;
}

export const getSpaceUserListForFolder = createAsyncThunk(
  "accessPermission/getSpaceUserListForFolder",
  async (_, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.get<WithNetworkFailure<GetFolderRolesListResponse>>(
        `${getBackendUrl()}/folders/getFolderRolesList`,
      );
      if (responseData.data.status) {
        return responseData.data.result;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(rejectionPayload(error));
    }
  },
);

export const setAccessPermission = createAsyncThunk(
  "accessPermission/setAccessPermission",
  async (data: { fileVersionId: string; spaceId: string }, { rejectWithValue }) => {
    // The ONE isFile403 call. The whole result is stored in this slice so the
    // viewer's load gates and indicators read it from here instead of calling again.
    const access = await resolveFileAccess({ [data.fileVersionId]: { spaceId: data.spaceId } }, data.fileVersionId);
    // Tag the failure with the file it is about, so consumers can tell "this file's
    // check failed" (degrade to un-gated) from "still in flight" (wait).
    if (access.status !== true) return rejectWithValue({ ...access, fileVersionId: data.fileVersionId });
    return access;
  },
);

export const setSpaceToggle = createAsyncThunk(
  "accessPermission/setSpaceToggle",
  // Forwarded verbatim, and `SpaceToggleRequest` is the branded output type.
  async (data: object, { rejectWithValue }) => {
    try {
      const responseData = await axiosWrapper.post<SpaceToggleResponse>(`${getBackendUrl()}/folders/spaceToggle`, data);
      if (responseData.data.status) {
        return responseData.data;
      }
      return rejectWithValue(responseData.data);
    } catch (error: unknown) {
      return rejectWithValue(rejectionPayload(error));
    }
  },
);

/**
 * The response schema is a `standardResponse(...)` discriminated union, so `result` lives on the
 * success arm alone — drilled through `Extract` rather than indexed off the union.
 */
type FolderRoleCatalog = Extract<GetFolderRolesListResponse, { status: true }>["result"];

// This slice was created with a bare `initialState: []`, which made its state the untyped
// `never[]` — so every write had to be asserted back to `never[]` and consumers could read any field
// off its rows unchecked. It now carries the element type the thunk that fills it actually resolves
// with, and the writes need no assertion.

/** The folder-role CATALOG from `getFolderRolesList` — role definitions, not users. */
const spaceUserDataForFolderSlice = createSlice({
  name: "spaceUserDataForFolder",
  initialState: [] as FolderRoleCatalog,
  reducers: {
    setSpaceUserListForFolder: (_state, action: PayloadAction<FolderRoleCatalog>) => action.payload,
  },
  extraReducers: (builder) => {
    builder.addCase(getSpaceUserListForFolder.fulfilled, (_state, action) => action.payload);
  },
});

const accessPermissionSlice = createSlice({
  name: "accessPermission",
  initialState: {},
  reducers: {
    // The payload is the `resolveFileAccess` result the thunk of the same name resolves with; this
    // reducer is the manual entry point onto the same slot, so it stores the same shape.
    setAccessPermission: (_state, action: PayloadAction<FileAccessResult>) => action.payload,
  },
  extraReducers: (builder) => {
    // `requestedFor` records the file the newest dispatch is about, and settlements for any other file are
    // DROPPED. Without that, two checks in flight across a file switch settle last-write-wins: a late
    // answer for the previous file would land in the store, selectFileAccessFor would then return
    // undefined for the current file forever (nothing re-dispatches), and every gated consumer would wait
    // indefinitely.
    // `pending` deliberately keeps the existing payload rather than clearing it: selectFileAccessFor
    // already scopes the gating fields by fileVersionId, while the raw `data` / `canViewFile` readers
    // (DesignViewer's permission map and view gate) must not be blanked mid-switch.
    // The slice state is the `{}` inferred from `initialState`, so `requestedFor` is not a declared member
    // of it — every read below goes through the same erased view alias.
    const isCurrent = (
      state: { requestedFor?: string } | undefined,
      action: { meta?: { arg?: { fileVersionId?: string } } },
    ) => action.meta?.arg?.fileVersionId === state?.requestedFor;
    builder
      .addCase(setAccessPermission.pending, (state, action) => ({
        ...state,
        requestedFor: action.meta.arg.fileVersionId,
      }))
      .addCase(setAccessPermission.rejected, (state, action) =>
        isCurrent(state, action)
          ? {
              ...(action.payload ?? {}),
              requestedFor: (state as { requestedFor?: string }).requestedFor,
            }
          : state,
      )
      .addCase(setAccessPermission.fulfilled, (state, action) =>
        isCurrent(state, action)
          ? { ...action.payload, requestedFor: (state as { requestedFor?: string }).requestedFor }
          : state,
      );
  },
});

/**
 * The isFile403 result, but only when it is about `fileVersionId`. Returns undefined
 * while a check is in flight, after a failure, or when the store still holds the
 * previously opened file — so callers fall back to un-gated behaviour instead of
 * applying one file's permissions to another.
 *
 * `fileVersionId` is optional because callers read it from a prop or the viewer
 * context, where it isn't guaranteed yet; with no id there is nothing to match.
 */
export const selectFileAccessFor = (
  state: RootState,
  fileVersionId: string | undefined,
): FileAccessResult | undefined => {
  if (!fileVersionId) return undefined;
  const access = state.getAccessPermission as FileAccessResult;
  return access.status === true && access.fileVersionId === fileVersionId ? access : undefined;
};

const initialSpaceToggleState: Partial<SpaceToggleResponse> = {};

const spaceToggleSlice = createSlice({
  name: "spaceToggle",
  // Empty until the first `setSpaceToggle` settles, so every field is optional until then.
  initialState: initialSpaceToggleState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setSpaceToggle.fulfilled, (_state, action) => action.payload);
  },
});

export const spaceUserDataForFolder = spaceUserDataForFolderSlice.reducer;
export const getAccessPermission = accessPermissionSlice.reducer;
export const getSpaceToggle = spaceToggleSlice.reducer;
